import { useEffect, useRef } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { AccessDenied } from 'shared/errors/access-denied'
import { NotFound } from 'shared/errors/not-found'
import { UnprocessableContentError } from 'shared/errors/unprocessable-content-error'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { useInvitationValidity } from '../api/check-invitation-validity'
import type { InvalidInvitationLinkReason } from '../components/invalid-invitation-link/invalid-invitation-link'
import { useAcceptInvitationFlow } from '../members/hooks/use-accept-invitation-flow'
import type { InvitationValidityInterface } from '../types/invitation-interface'

// TODO: redesign this whole file

export const resolveInvitationErrorReason = (
  error: Error | null,
): InvalidInvitationLinkReason | null => {
  if (!error) {
    return null
  }
  if (error instanceof UnprocessableContentError) {
    return 'expired'
  }
  if (error instanceof NotFound) {
    return 'canceled'
  }
  if (error instanceof AccessDenied) {
    return 'mismatch'
  }
  return 'invalid'
}

interface UseInvitationFlowOptions {
  communityPath: string
  invitationToken: string
  confirmationToken?: string
  showInvalidInvitation?: string
}

interface InvitationFlowState {
  validity: UseQueryResult<InvitationValidityInterface>
  showInvalidReason: InvalidInvitationLinkReason | undefined
}

const VALID_REASONS: readonly InvalidInvitationLinkReason[] = [
  'canceled',
  'expired',
  'invalid',
  'mismatch',
]

export function useInvitationFlow({
  communityPath,
  invitationToken,
  confirmationToken,
  showInvalidInvitation,
}: UseInvitationFlowOptions): InvitationFlowState {
  const router = useRouter()
  const { user, loading: userLoading } = useUser({ unauthorizedResponseHandler: () => {} })
  const autoAcceptTried = useRef(false)
  const onboardingRedirectTried = useRef(false)
  const acceptedRedirectTried = useRef(false)

  const validity = useInvitationValidity({
    communityPath,
    invitationToken,
    confirmationToken,
  })

  const acceptInvitation = useAcceptInvitationFlow({ communityPath })

  const isInvalidInvitationReason = (value: string): value is InvalidInvitationLinkReason =>
    (VALID_REASONS as readonly string[]).includes(value)

  const showInvalidReason: InvalidInvitationLinkReason | undefined = showInvalidInvitation
    ? isInvalidInvitationReason(showInvalidInvitation)
      ? showInvalidInvitation
      : 'invalid'
    : undefined

  useEffect(() => {
    if (!invitationToken || autoAcceptTried.current) {
      return
    }
    if (validity.isLoading || !validity.data || userLoading) {
      return
    }

    const { status } = validity.data

    if (status === 'accepted') {
      if (acceptedRedirectTried.current) {
        return
      }
      acceptedRedirectTried.current = true
      globalThis.location.replace(`/community/${communityPath}`)
      return
    }

    if (status === 'expired' || status === 'canceled') {
      return
    }

    if (status === 'pending' && !validity.data.userDomainAccountConfirmed) {
      if (onboardingRedirectTried.current) {
        return
      }
      onboardingRedirectTried.current = true
      router.replace(`/community/${communityPath}/register`)
      return
    }

    if (!isAuthorizedUser(user)) {
      const params = new URLSearchParams()
      params.set('invitationToken', invitationToken)
      if (confirmationToken) {
        params.set('confirmationToken', confirmationToken)
      }
      const query = params.toString()
      const returnUrl = `/community/${communityPath}${query ? `?${query}` : ''}`
      globalThis.location.replace(`/login?redirect_url=${encodeURIComponent(returnUrl)}`)
      return
    }

    autoAcceptTried.current = true
    void acceptInvitation({
      invitationToken,
      confirmationToken,
    }).catch((error: Error) => {
      const reason = resolveInvitationErrorReason(error) ?? 'invalid'
      globalThis.location.replace(`/community/${communityPath}?showInvalidInvitation=${reason}`)
    })
  }, [
    acceptInvitation,
    autoAcceptTried,
    acceptedRedirectTried,
    communityPath,
    confirmationToken,
    invitationToken,
    onboardingRedirectTried,
    router,
    user,
    userLoading,
    validity.data,
    validity.isLoading,
  ])

  return { validity, showInvalidReason }
}
